import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { DataTable, FilterBar, FilterBarChip, SortState } from '../patterns';
import { listTransactions, InventoryTransaction } from '../../services/consumableInventoryService';
import type { ConsumableType } from '../../services/consumableService';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDate } from '../../dateFormat';

type TxTypeFilter = 'all' | InventoryTransaction['type'];

const TYPE_CHIPS_CONSUMABLE: { id: ConsumableType; labelKey: string }[] = [
  { id: 'chemical',   labelKey: 'consumables.catalog.filters.type.chemical' },
  { id: 'ppe',        labelKey: 'consumables.catalog.filters.type.ppe' },
  { id: 'fertilizer', labelKey: 'consumables.catalog.filters.type.fertilizer' },
  { id: 'diesel',     labelKey: 'consumables.catalog.filters.type.diesel' },
];

export interface StockMovementsTableProps {
  /** When set, hides the consumableType chip filter + locks the param. */
  scope?: ConsumableType;
  /** Optional per-product drilldown — passed straight through to the listTransactions query. */
  productId?: string;
}

const StockMovementsTable: React.FC<StockMovementsTableProps> = ({ scope, productId }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [txTypeFilter, setTxTypeFilter] = useState<TxTypeFilter>('all');
  const [activeConsumableChip, setActiveConsumableChip] = useState<ConsumableType | null>(null);
  // Default to date/time descending (newest movement first); every column sorts.
  const [sort, setSort] = useState<SortState>({ key: 'date', direction: 'desc' });

  const effectiveConsumableType: ConsumableType | undefined = scope ?? activeConsumableChip ?? undefined;

  useEffect(() => {
    setLoading(true);
    setError('');
    const params: { consumableType?: ConsumableType; productId?: string } = {};
    if (effectiveConsumableType) params.consumableType = effectiveConsumableType;
    if (productId) params.productId = productId;
    listTransactions(params)
      .then((data) => { setRows(data); setLoading(false); })
      .catch((err: { message?: string }) => { setError(err.message ?? t('common.error', { defaultValue: 'Failed to load' })); setLoading(false); });
    // t() is read at error time only; intentionally excluded from deps so a
    // changing t identity doesn't retrigger the fetch (exhausts test mocks).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveConsumableType, productId]);

  const txChips: FilterBarChip[] = [
    { id: 'all',         label: t('spray.filters.all', { defaultValue: 'All' }),                          active: txTypeFilter === 'all' },
    { id: 'purchase',    label: t('spray.inventory.purchase', { defaultValue: 'Purchase' }),              active: txTypeFilter === 'purchase' },
    { id: 'consumption', label: t('spray.inventory.consumption', { defaultValue: 'Consumption' }),        active: txTypeFilter === 'consumption' },
    { id: 'adjustment',  label: t('spray.inventory.adjustment', { defaultValue: 'Adjustment' }),          active: txTypeFilter === 'adjustment' },
  ];

  const consumableChips: FilterBarChip[] = TYPE_CHIPS_CONSUMABLE.map((c) => ({
    id: c.id,
    label: t(c.labelKey),
    active: activeConsumableChip === c.id,
  }));

  const allChips: FilterBarChip[] = scope ? txChips : [...txChips, ...consumableChips];

  const handleChipClick = (id: string) => {
    if (id === 'all' || id === 'purchase' || id === 'consumption' || id === 'adjustment') {
      setTxTypeFilter(id as TxTypeFilter);
      return;
    }
    if (!scope && (id === 'chemical' || id === 'ppe' || id === 'fertilizer' || id === 'diesel')) {
      setActiveConsumableChip((prev) => (prev === id ? null : (id as ConsumableType)));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((tx) => {
      if (txTypeFilter !== 'all' && tx.type !== txTypeFilter) return false;
      if (!q) return true;
      return (
        tx.date.toLowerCase().includes(q) ||
        (tx.referenceLabel ?? tx.referenceId ?? '').toLowerCase().includes(q) ||
        (tx.consumable?.productName ?? '').toLowerCase().includes(q) ||
        (tx.supplier ?? '').toLowerCase().includes(q) ||
        tx.type.toLowerCase().includes(q)
      );
    });
  }, [rows, search, txTypeFilter]);

  // Client-side sort over the filtered rows. DataTable is presentational — the
  // parent owns sort state + ordering. Strings use numeric-aware localeCompare;
  // 'date' compares timestamps with createdAt as a same-day time tiebreaker;
  // 'quantity' is numeric. Default is date descending (newest first).
  const sortedRows = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const cmpStr = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    const refOf = (tx: InventoryTransaction) => tx.referenceLabel ?? tx.referenceId ?? '';
    return [...filtered].sort((a, b) => {
      switch (sort.key) {
        case 'type':      return cmpStr(a.type, b.type) * dir;
        case 'product':   return cmpStr(a.consumable?.productName ?? '', b.consumable?.productName ?? '') * dir;
        case 'quantity':  return (a.quantity - b.quantity) * dir;
        case 'reference': return cmpStr(refOf(a), refOf(b)) * dir;
        case 'supplier':  return cmpStr(a.supplier ?? '', b.supplier ?? '') * dir;
        case 'date':
        default: {
          const at = new Date(a.date).getTime();
          const bt = new Date(b.date).getTime();
          if (at !== bt) return (at - bt) * dir;
          const ac = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bc = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return (ac - bc) * dir;
        }
      }
    });
  }, [filtered, sort]);

  const formatProduct = (tx: InventoryTransaction): string => {
    if (!tx.consumable) return t('consumables.shared.deletedProduct', { defaultValue: '(deleted product)' });
    const base = tx.consumable.lNumber
      ? `${tx.consumable.productName} (${tx.consumable.lNumber})`
      : tx.consumable.productName;
    return tx.consumable.deletedAt
      ? `${base} ${t('consumables.shared.deletedSuffix', { defaultValue: '(deleted)' })}`
      : base;
  };

  const formatQty = (tx: InventoryTransaction): string => {
    // We pass Math.abs(tx.quantity) into formatMeasure, so the sign that used
    // to fall out "for free" from a negative tx.quantity (e.g. consumption
    // rows) must be made explicit here — otherwise abs() would silently flip a
    // negative ledger quantity to a positive-looking one. formatMeasure also
    // rescales to a readable unit (18000 ml -> 18 L).
    const sign = tx.quantity > 0
      ? (tx.type === 'purchase' || tx.type === 'adjustment' ? '+' : '')
      : (tx.quantity < 0 ? '-' : '');
    const unit = tx.quantityUnit ?? '';
    return `${sign}${formatMeasure(Math.abs(tx.quantity), unit)}`;
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) return <Alert variant="danger" role="alert">{error}</Alert>;

  const emptyKey = scope === 'chemical' ? 'spray.stockMovements.empty' : 'consumables.stockMovements.empty';

  return (
    <div data-testid="stock-movements-table">
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={allChips}
        onChipClick={handleChipClick}
      />
      <DataTable<InventoryTransaction>
        rows={sortedRows}
        rowKey={(tx) => tx._id}
        sort={sort}
        onSortChange={setSort}
        columns={[
          { key: 'date',      label: t('consumables.stockMovements.columns.date'),      sortable: true, render: (tx) => formatDate(tx.date) },
          { key: 'type',      label: t('consumables.stockMovements.columns.type'),      sortable: true, render: (tx) => t(`spray.inventory.${tx.type}`, { defaultValue: tx.type }) },
          { key: 'product',   label: t('consumables.stockMovements.columns.product'),   sortable: true, render: formatProduct },
          { key: 'quantity',  label: t('consumables.stockMovements.columns.quantity'),  align: 'right', sortable: true, render: formatQty },
          {
            key: 'reference',
            label: t('consumables.stockMovements.columns.reference'),
            sortable: true,
            render: (tx) => (
              <>
                {tx.referenceLabel ?? tx.referenceId ?? ''}
                {tx.referenceStatus === 'orphaned' && (
                  <span className="text-warning ms-1">
                    {t('consumables.shared.fromDeletedSession', { defaultValue: '(from deleted session)' })}
                  </span>
                )}
              </>
            ),
          },
          { key: 'supplier',  label: t('consumables.stockMovements.columns.supplier'),  sortable: true, render: (tx) => tx.supplier ?? '' },
        ]}
        emptyState={t(emptyKey)}
        mobileCardRow={(tx) => (
          <div>
            <strong>
              {formatDate(tx.date)} · {t(`spray.inventory.${tx.type}`, { defaultValue: tx.type })}
            </strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>{formatProduct(tx)}</div>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {formatQty(tx)} · {tx.referenceLabel ?? tx.referenceId ?? ''}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default StockMovementsTable;
